import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC, Fragment, useEffect, useState } from 'react'
import { Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { toast } from 'react-toastify'
import {
    getUploadDataInventoryAsync,
    uploadInventoryCsvAsync,
} from 'src/redux/actions/omsProductActions'
import { useAppDispatch } from 'src/redux/storeHooks'
import { accessSessionTokenKeyName } from 'src/utils/constants'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import StatusButton from '../orders/StatusButton'
import { extractAPIErrorMessage } from 'src/utils/extractAPIErrorMessage'

type UploadRow = {
    id: number
    identifier?: string | null
    sync_status?: string
    created_at?: string
    updated_at?: string
    fileUrl: string
    message?: string
    feature?: string
    filename?: string
}

type Props = {
    show: boolean
    isActive?: boolean
    handleClose: () => void
    platformId: string | undefined
    connectedPlatformTypes?: Record<string, any> | null
    onBusyChange?: (busy: boolean) => void
}

const INVENTORY_SAMPLE_TYPE = 'product_inventory_upload_sample'

const UploadCsvInventory: FC<Props> = ({
    show,
    isActive,
    handleClose,
    platformId,
    onBusyChange,
}) => {
    const dispatch = useAppDispatch()
    const isTabActive = isActive ?? show

    const [file, setFile] = useState<File | null>(null)
    const [invalidFileType, setInvalidFileType] = useState(false)
    const [loadingList, setLoadingList] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadList, setUploadList] = useState<UploadRow[]>([])
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [totalRows, setTotalRows] = useState(0)
    const [downloadingTemplate, setDownloadingTemplate] = useState(false)

    const customStyles = {
        tableWrapper: {
            style: {
                border: '1px solid gainsboro',
                borderRadius: '8px',
                overflow: 'hidden',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid gainsboro',
                minHeight: '44px',
            },
        },
        headCells: {
            style: {
                fontWeight: 700,
                fontSize: '13px',
            },
        },
        rows: {
            style: {
                minHeight: '48px',
            },
        },
        cells: {
            style: {
                paddingTop: '10px',
                paddingBottom: '10px',
                borderBottom: '1px solid gainsboro',
                fontSize: '13px',
            },
        },
        pagination: {
            style: {
                minHeight: '10px',
            },
        },
    }

    const fetchHistory = async () => {
        try {
            if (!platformId) return
            setLoadingList(true)

            const res = await dispatch(
                getUploadDataInventoryAsync({ id: Number(platformId), pag: page, limit: limit })
            )

            const payload = res.payload as any
            if (payload?.status && payload.status >= 400) {
                toast.error(extractAPIErrorMessage(payload, 'Failed to fetch upload history'))
                return
            }
            const list = payload?.results?.data
            setUploadList(Array.isArray(list) ? list : [])
            setTotalRows(payload?.count ?? 0)
        } catch (err) {
            toast.error(extractAPIErrorMessage(err, 'Failed to fetch upload history'))
        } finally {
            setLoadingList(false)
        }
    }

    useEffect(() => {
        if (isTabActive) {
            fetchHistory()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabActive, platformId, page, limit])

    useEffect(() => {
        if (!show) {
            setFile(null)
            setInvalidFileType(false)
            setPage(1)
            setLimit(10)
        }
    }, [show])

    useEffect(() => {
        onBusyChange?.(uploading || loadingList)
    }, [uploading, loadingList, onBusyChange])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (!selectedFile) return

        const ext = selectedFile.name.split('.').pop()?.toLowerCase()
        if (!ext || ext !== 'csv') {
            setInvalidFileType(true)
            setFile(null)
            event.target.value = ''
            toast.warning('Please upload a file in .csv format.')
            return
        }

        setInvalidFileType(false)
        setFile(selectedFile)
    }

    const handleTemplateDownload = async () => {
        if (!platformId) {
            toast.warning('Platform id missing')
            return
        }

        setDownloadingTemplate(true)

        try {
            const token = localStorage.getItem(accessSessionTokenKeyName)
            const url = `${process.env.REACT_APP_API_URL}/api/download-bulk-sample?platform_id=${platformId}&type=${INVENTORY_SAMPLE_TYPE}`

            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'text/csv,application/octet-stream,*/*',
                    Authorization: token ? `Bearer ${token}` : '',
                },
            })

            if (!res.ok) {
                const errorPayload: any = { status: res.status }
                const errorText = await res.text()
                if (errorText) {
                    try {
                        errorPayload.data = JSON.parse(errorText)
                    } catch {
                        errorPayload.data = { message: errorText }
                    }
                }
                toast.error(extractAPIErrorMessage(errorPayload, 'Unable to download template'))
                return
            }

            const blob = await res.blob()
            const blobUrl = window.URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = blobUrl
            anchor.download = 'inventory_upload_template.csv'
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            window.URL.revokeObjectURL(blobUrl)
            toast.success('Template downloaded successfully')
        } catch (err: any) {
            toast.error(extractAPIErrorMessage(err, 'Unable to download template'))
        } finally {
            setDownloadingTemplate(false)
        }
    }

    const handleUpload = async () => {
        if (!file) {
            toast.warning('Please choose a file to upload')
            return
        }
        if (!platformId) {
            toast.warning('Platform id missing')
            return
        }

        try {
            setUploading(true)
            const formData = new FormData()
            formData.append('platform_id', String(platformId))
            formData.append('file', file)

            const res = await dispatch(uploadInventoryCsvAsync(formData))
            const payload = res.payload as any
            const isSuccess = payload?.status === 200 || payload?.status === 201

            if (isSuccess) {
                toast.success(payload?.message ?? 'File uploaded successfully!')
                setFile(null)
                setInvalidFileType(false)
                fetchHistory()
                handleClose()
                return
            }

            const errorMessage = extractAPIErrorMessage(payload, 'Upload failed')
            toast.error(errorMessage)
        } catch (e: any) {
            const errorMessage = extractAPIErrorMessage(e, 'Upload failed')
            toast.error(errorMessage)
        } finally {
            setUploading(false)
        }
    }

    const columns = [
        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>File Name</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>File Name</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => {
                const filename = row.filename ?? '-'
                const shortFilename =
                    filename.length > 15 ? `${filename.slice(0, 15)}...` : filename

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id={`tooltip-inventory-filename-${row.id}`}>
                                {filename}
                            </Tooltip>
                        }
                    >
                        <span style={{ cursor: 'pointer' }}>{shortFilename}</span>
                    </OverlayTrigger>
                )
            },
            width: '200px',
        },
        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>Upload Status</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Upload Status</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => (
                <StatusButton
                    syncStatus={row.sync_status ?? 'PENDING'}
                    syncMessage={row.message}
                />
            ),
            width: '170px',
            center: true,
        },
        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>Created At</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>{formatTimeZone()}</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => (
                <p style={{ margin: 0 }}>
                    {row.created_at ? formatDateLocal(row.created_at) : ''}
                </p>
            ),
            right: true,
            width: '170px',
            center: true,
        },
        {
            name: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span>Updated At</span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>{formatTimeZone()}</Tooltip>}>
                        <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            cell: (row: UploadRow) => (
                <p style={{ margin: 0 }}>
                    {row.updated_at ? formatDateLocal(row.updated_at) : ''}
                </p>
            ),
            right: true,
            width: '170px',
            center: true,
        },
        {
            name: 'Action',
            cell: (row: UploadRow) => (
                <div className="d-flex justify-content-center w-100">
                    <a
                        href={row.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        title="Download"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center upload-csv-download-btn"
                        style={{ width: '32px', height: '32px', border: 'none' }}
                    >
                        <i className="bi bi-cloud-arrow-down fs-5"></i>
                    </a>
                </div>
            ),
            width: '100px',
            center: true,
        },
    ]

    return (
        <Fragment>
            <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
                <div className="d-flex align-items-end gap-8">
                    <div className="d-flex flex-column" style={{ minWidth: 320 }}>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                            <span className="text-muted" style={{ fontSize: 12 }}>
                            Upload your stock/inventory file
                            </span>
                            <span
                                className={`small ${invalidFileType ? 'text-danger' : 'text-muted'}`}
                                style={{ fontSize: 11 }}
                            >
                                Accepted: .csv
                            </span>
                        </div>

                        <div
                            className="d-flex align-items-center justify-content-between gap-2"
                            style={{
                                border: `1px solid ${invalidFileType ? '#dc3545' : '#dee2e6'}`,
                                borderRadius: 10,
                                padding: '6px 8px',
                                background: '#fff',
                            }}
                        >
                            <label
                                htmlFor="inventory-upload"
                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                <i className="bi bi-upload me-2"></i>
                                Choose file
                            </label>

                            <div
                                className="d-flex align-items-center gap-2 flex-grow-1 justify-content-end"
                                style={{ minWidth: 0 }}
                            >
                                {file ? (
                                    <span
                                        className="d-inline-flex align-items-center"
                                        title={file.name}
                                        style={{
                                            maxWidth: 260,
                                            padding: '4px 10px',
                                            borderRadius: 999,
                                            border: '1px solid #dee2e6',
                                            background: '#f8f9fa',
                                            fontSize: 12,
                                            minWidth: 0,
                                        }}
                                    >
                                        <i className="bi bi-file-earmark-text me-2"></i>
                                        <span className="text-truncate" style={{ maxWidth: 190 }}>
                                            {file.name}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 ms-2 text-muted"
                                            style={{ lineHeight: 1, textDecoration: 'none' }}
                                            title="Remove file"
                                            onClick={() => {
                                                setFile(null)
                                                setInvalidFileType(false)
                                                const input = document.getElementById(
                                                    'inventory-upload'
                                                ) as HTMLInputElement | null
                                                if (input) input.value = ''
                                            }}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </span>
                                ) : (
                                    <span
                                        className="text-muted text-truncate"
                                        style={{ fontSize: 12, maxWidth: 260 }}
                                    >
                                        No file selected
                                    </span>
                                )}
                            </div>

                            <input
                                id="inventory-upload"
                                type="file"
                                className="d-none"
                                accept=".csv"
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>

                    <Button
                        variant="primary"
                        size="sm"
                        disabled={uploading || !file}
                        onClick={handleUpload}
                        style={{ whiteSpace: 'nowrap', height: '45px' }}
                    >
                        {uploading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Uploading...
                            </>
                        ) : (
                            'Upload'
                        )}
                    </Button>
                </div>

                <div className="d-flex align-items-center gap-2" style={{ minWidth: 260 }}>
                    <span className="text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        Download template for{' '}
                        <span className="fw-semibold text-dark">Commercium OMS</span>:
                    </span>

                    <button
                        type="button"
                        className="d-inline-flex align-items-center justify-content-center btn btn-light border"
                        title="Download template"
                        disabled={!platformId || downloadingTemplate}
                        onClick={handleTemplateDownload}
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            padding: 0,
                        }}
                    >
                        {downloadingTemplate ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <i className="bi bi-cloud-arrow-down text-primary fs-5"></i>
                        )}
                    </button>
                </div>
            </div>

            <h5 className="mb-3 fw-bold text-dark">Upload History</h5>
            <div style={{ height: '40vh' }}>
                <TableLoadingShell loading={loadingList} minHeight="30vh">
                <DataTable
                    title=""
                    columns={columns as any}
                    data={uploadList}
                    progressPending={false}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={(newPage) => setPage(newPage)}
                    onChangeRowsPerPage={(newPerPage) => {
                        setLimit(newPerPage)
                        setPage(1)
                    }}
                    fixedHeader
                    fixedHeaderScrollHeight="30vh"
                    customStyles={customStyles}
                />
                </TableLoadingShell>
            </div>
        </Fragment>
    )
}

export default UploadCsvInventory
